
















import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import CameraPreview from '@/components/CameraPreview.vue';
import { setTimeoutAsync } from '@/services/utils';
import {
  DirectionDetectionPhase,
  FaceTrackerServer,
  Direction,
  TrackerStatus,
  TrackerType,
  Rotation,
  ErrorType,
  FaceResult,
  FPS,
  CalibrationResult,
  CalibrationStatus
} from '@/services/facetracker';
import api, { CheckDirectionStatus } from '@/api/facetracker/FaceTracker';
import FaceDirectionArrow from '@/components/facetracker/FaceDirectionArrow.vue';
import ScreenOrientationTracker from '@/components/ScreenOrientationTracker.vue';
import { ScreenOrientation } from '@/resources/enum/ScreenOrientation';

export interface TrackerErrorEventArgs {
  errorType: ErrorType;
  isManyRetry: boolean;
}

/** 首振りの状態を管理するレイヤー */
@Component({
  components: { CameraPreview, ScreenOrientationTracker }
})
export default class FaceDirectionTracker extends Vue {
  @Prop({ default: TrackerType.Server })
  trackerType!: TrackerType;

  $refs!: {
    preview: CameraPreview;
    overlay: HTMLCanvasElement;
  };

  private tracker!: FaceTrackerServer;

  private statusInternal = TrackerStatus.None;

  private set status(status: TrackerStatus) {
    if (this.statusInternal !== status) {
      this.statusInternal = status;
      this.statusChanged();
    }
  }

  private get status(): TrackerStatus {
    return this.statusInternal;
  }

  private directionPhaseInternal = DirectionDetectionPhase.None;

  private set directionPhase(step: DirectionDetectionPhase) {
    if (this.directionPhaseInternal !== step) {
      this.directionPhaseInternal = step;
      this.directionPhaseChanged();
    }
  }

  private get directionPhase(): DirectionDetectionPhase {
    return this.directionPhaseInternal;
  }

  private directionStatusInternal = CheckDirectionStatus.None;

  private set directionStatus(status: CheckDirectionStatus) {
    if (this.directionStatusInternal !== status) {
      this.directionStatusInternal = status;
      this.directionStatusChanged();
    }
  }

  private get directionStatus(): CheckDirectionStatus {
    return this.directionStatusInternal;
  }

  private fps = new FPS(10);

  async mounted() {
    switch (this.trackerType) {
      case TrackerType.Server:
        this.tracker = new FaceTrackerServer(this.$refs.preview.video, document.createElement('canvas'));
        break;
      default:
        console.error(`Tracker type: ${this.trackerType} is not supported`); // eslint-disable-line no-console
    }
    await this.tracker.load();
  }

  beforeDestroy() {
    this.tracker.dispose();
  }

  get preview(): CameraPreview {
    return this.$refs.preview;
  }

  get currentStatus(): TrackerStatus {
    return this.status;
  }

  get currentDirectionStatus(): CheckDirectionStatus {
    return this.directionStatus;
  }

  calibrate() {
    if (this.status === TrackerStatus.Calibrating) {
      this.status = TrackerStatus.Detecting;
    }
  }

  public async reInitialized() {
    const isSuccess: boolean = await this.tracker.videoReInitialized();
    if (!isSuccess) throw new Error('FaceDirectionTracker reInitialized');
  }

  reset() {
    if (this.orientation === ScreenOrientation.Landscape) {
      this.status = TrackerStatus.InError;
      this.error({ errorType: ErrorType.InvalidScreenOrientation, isManyRetry: false });
      return;
    }

    this.status = TrackerStatus.EstablishingSession;
    this.directionPhase = DirectionDetectionPhase.None;
    this.directionStatus = CheckDirectionStatus.None;
    this.directionsChanged([]);
  }

  async update() {
    switch (this.status) {
      case TrackerStatus.None:
      case TrackerStatus.Ended:
        return;

      case TrackerStatus.EstablishingSession:
        this.status = TrackerStatus.Calibrating;
        return;

      case TrackerStatus.Calibrating: {
        const result = await this.tracker.calibrate();
        this.calibrateProgress(result.currentCount, result.maxCount);
        switch (result.status) {
          case CalibrationStatus.Processing:
            return;

          case CalibrationStatus.Completed:
            this.status = TrackerStatus.TakingSelfie;
            return;

          case CalibrationStatus.InError:
          default:
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: result.isManyRetry || false });
            return;
        }
      }

      case TrackerStatus.TakingSelfie: {
        const result = await this.tracker.takeSelfie();
        this.calibrateProgress(0, 1);
        if (result.isSuccess) {
          this.status = TrackerStatus.InitializingDirections;
        } else {
          this.status = TrackerStatus.InError;
          this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: false });
        }
        return;
      }

      case TrackerStatus.InitializingDirections: {
        const result = await this.tracker.initializeDirections();
        if (!result.error) {
          this.directionsChanged(result.directions.sort((a, b) => a.index - b.index).map(a => a.direction));
          this.status = TrackerStatus.Detecting;
        } else {
          this.status = TrackerStatus.InError;
          this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: false });
        }
        return;
      }

      case TrackerStatus.Detecting: {
        if (this.directionPhase === DirectionDetectionPhase.None) {
          this.directionPhase = DirectionDetectionPhase.Phase1;
          return;
        }

        const result = await this.tracker.detect(this.directionPhase);

        switch (result.result) {
          case CheckDirectionStatus.Success:
            this.directionStatus = result.result;
            this.incrementDirectionPhase();
            if (this.directionPhase === DirectionDetectionPhase.Completed) {
              this.status = TrackerStatus.Ended;
            }
            this.directionStatus = CheckDirectionStatus.None; // reset
            return;

          case CheckDirectionStatus.Ignore:
          case CheckDirectionStatus.None:
            this.directionStatus = result.result;
            return;

          case CheckDirectionStatus.DifferentDirection:
          case CheckDirectionStatus.FaceNotFound:
            this.directionStatus = result.result;
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: result.isManyRetry || false });
            return;

          case CheckDirectionStatus.InError:
          default:
            this.status = TrackerStatus.InError;
            this.error({ errorType: result.error || ErrorType.Unknown, isManyRetry: result.isManyRetry || false });
            return;
        }
      }
    }
  }

  @Emit()
  private error(e: TrackerErrorEventArgs) {
    //
    this.calibrateProgress(0, 1);
  }

  private async initialized(width: number, height: number) {
    const isSuccess: boolean = await this.tracker.videoInitialized(width, height);
    if (!isSuccess) throw new Error('FaceDirectionTracker initialized');
    this.status = TrackerStatus.EstablishingSession;
  }

  @Emit()
  private statusChanged() {
    return this.status;
  }

  @Emit()
  private directionPhaseChanged() {
    return this.directionPhase;
  }

  @Emit()
  private directionStatusChanged() {
    return this.directionStatus;
  }

  @Emit()
  private directionsChanged(value: Direction[]) {}

  private onPreviewError(err: any) {
    console.error('got preview error', err); // eslint-disable-line no-console
  }

  private orientation: ScreenOrientation = ScreenOrientation.None;

  private onScreenTrackerOrientationChanged(orientation: ScreenOrientation) {
    this.orientation = orientation;
  }

  @Watch('orientation')
  private onOrientationChanged(value: ScreenOrientation) {
    if (value === ScreenOrientation.Landscape) {
      this.status = TrackerStatus.InError;
      this.error({ errorType: ErrorType.InvalidScreenOrientation, isManyRetry: false });
    }
  }

  @Emit()
  private previewPermissionDenied(err: any) {
    console.error('got PermissionDenied error', err); // eslint-disable-line no-console
  }

  @Emit()
  private calibrateProgress(currentCount: number, maxCount: number) {}

  private incrementDirectionPhase() {
    switch (this.directionPhase) {
      case DirectionDetectionPhase.None:
        this.directionPhase = DirectionDetectionPhase.Phase1;
        return;
      case DirectionDetectionPhase.Phase1:
        this.directionPhase = DirectionDetectionPhase.Center1;
        return;
      case DirectionDetectionPhase.Center1:
        this.directionPhase = DirectionDetectionPhase.Phase2;
        return;
      case DirectionDetectionPhase.Phase2:
        this.directionPhase = DirectionDetectionPhase.Center2;
        return;
      case DirectionDetectionPhase.Center2:
        this.directionPhase = DirectionDetectionPhase.Phase3;
        return;
      case DirectionDetectionPhase.Phase3:
        this.directionPhase = DirectionDetectionPhase.Completed;
        return;
      case DirectionDetectionPhase.Completed:
        return;
      default:
        return;
    }
  }
}
